import { type FC, useState } from "react";
import { type ContentFileDto, ContentType } from "shared";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, type SwiperClass, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "./PreviewMedia.css";

interface PreviewMediaProps {
	files: ContentFileDto[];
	aspectRatio?: "1:1" | "16:9" | "4:5" | "9:16" | "auto";
	/** Shown on video elements until playback starts (e.g. YouTube custom thumbnail). */
	posterUrl?: string;
}

const MediaItem: FC<{ file: ContentFileDto; posterUrl?: string }> = ({
	file,
	posterUrl,
}) => {
	return (
		<>
			{file.type === ContentType.IMAGE && (
				<img src={file.url} alt={file.filename} />
			)}
			{file.type === ContentType.VIDEO && (
				<video muted controls poster={posterUrl}>
					<source src={file.url} type="video/mp4" />
				</video>
			)}
		</>
	);
};

export const PreviewMedia: FC<PreviewMediaProps> = ({
	files,
	aspectRatio = "auto",
	posterUrl,
}) => {
	const [, setSwiper] = useState<SwiperClass | null>(null);

	if (files.length === 0) {
		return (
			<div
				className={`preview-media preview-media--empty preview-media--${aspectRatio}`}
			>
				<span className="preview-media__placeholder">No media attached</span>
			</div>
		);
	}

	if (files.length === 1) {
		return (
			<div
				className={`preview-media preview-media--single preview-media--${aspectRatio}`}
			>
				<MediaItem file={files[0]} posterUrl={posterUrl} />
			</div>
		);
	}

	return (
		<div
			className={`preview-media preview-media--carousel preview-media--${aspectRatio}`}
		>
			<Swiper
				modules={[Navigation, Pagination]}
				slidesPerView={1}
				pagination={{ clickable: true }}
				onBeforeInit={(swiper: SwiperClass) => setSwiper(swiper)}
				loop
			>
				{files.map((file) => (
					<SwiperSlide key={file.id}>
						<MediaItem file={file} posterUrl={posterUrl} />
					</SwiperSlide>
				))}
			</Swiper>
		</div>
	);
};
